import { useState } from 'react';

import { useMutation, useSuspenseQuery } from '@tanstack/react-query';

import {
    feesSettingsOptions,
    useUpdateFeesSettingsOptions,
} from '@/entities/client-billing-settings';
import { SettingsEditableRow } from '@/widgets/settings/settings-editable-row';
import { SettingsSection } from '@/widgets/settings/settings-section';

import { formatFeePercent } from '../../lib/format-fee-percent';
import { parseFeeBasisPoints } from '../../lib/parse-fee-basis-points';
import { sanitizeFeePercentInput } from '../../lib/sanitize-fee-percent-input';
import { feePercentSchema } from '../../model/fee-percent.schema';

type FeeField = 'lateCancel' | 'noShow';

const renderFeeCaption = (value: string) => `${value}% of service fee`;

export function FeesSection() {
    const { data } = useSuspenseQuery(feesSettingsOptions());
    const updateMutation = useMutation(useUpdateFeesSettingsOptions());
    const [editingField, setEditingField] = useState<FeeField | null>(null);

    const handleSaveLateCancel = async (text: string) => {
        const basisPoints = parseFeeBasisPoints(text);
        if (basisPoints === null) return;
        await updateMutation.mutateAsync({ lateCancelBasisPoints: basisPoints });
    };

    const handleSaveNoShow = async (text: string) => {
        const basisPoints = parseFeeBasisPoints(text);
        if (basisPoints === null) return;
        await updateMutation.mutateAsync({ noShowBasisPoints: basisPoints });
    };

    return (
        <SettingsSection title="Fees" className="mb-12">
            <SettingsEditableRow
                label="Late cancel fee"
                value={formatFeePercent(data.value.lateCancelBasisPoints)}
                renderValue={renderFeeCaption}
                inputLabel="Percent of service fee"
                inputSuffix="%"
                placeholder=""
                maxLength={6}
                inputFilter={sanitizeFeePercentInput}
                inputMode="decimal"
                schema={feePercentSchema}
                onSave={handleSaveLateCancel}
                isDisabled={editingField !== null && editingField !== 'lateCancel'}
                onEditingChange={(isEditing) => setEditingField(isEditing ? 'lateCancel' : null)}
            />
            <SettingsEditableRow
                label="No show fee"
                value={formatFeePercent(data.value.noShowBasisPoints)}
                renderValue={renderFeeCaption}
                inputLabel="Percent of service fee"
                inputSuffix="%"
                placeholder=""
                maxLength={6}
                inputFilter={sanitizeFeePercentInput}
                inputMode="decimal"
                schema={feePercentSchema}
                onSave={handleSaveNoShow}
                isDisabled={editingField !== null && editingField !== 'noShow'}
                onEditingChange={(isEditing) => setEditingField(isEditing ? 'noShow' : null)}
            />
        </SettingsSection>
    );
}
